import React, { useState } from 'react';
import { useLang } from '../../context/LangContext';
import { ScanTarget } from '@shared/types';

interface SingleTargetInputProps {
  onTargetAdd: (target: ScanTarget) => void;
  disabled?: boolean;
}

// Parse user input: supports google.com, https://google.com, https://google.com/path, htpps://...
function parseTargetInput(raw: string): { hostname: string; port: number } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // If it looks like a URL (has ://) or starts with http, try URL parsing
  let urlStr = trimmed;
  // Fix common typos like htpps:// → https://
  urlStr = urlStr.replace(/^htpps?:\/\//i, 'https://');
  urlStr = urlStr.replace(/^https?:\/\//i, (m) => m.toLowerCase());

  // If no protocol, add one for URL parsing
  if (!/^https?:\/\//i.test(urlStr)) {
    urlStr = 'https://' + urlStr;
  }

  try {
    const parsed = new URL(urlStr);
    const hostname = parsed.hostname;
    const port = parsed.port ? parseInt(parsed.port) : 443;
    if (hostname) return { hostname, port };
  } catch {
    // fall through
  }

  // Fallback: treat as plain hostname/IP
  return { hostname: trimmed, port: 443 };
}

export const SingleTargetInput: React.FC<SingleTargetInputProps> = ({
  onTargetAdd,
  disabled = false,
}) => {
  const { lang } = useLang();
  const [target, setTarget] = useState('');
  const [port, setPort] = useState<number>(443);
  const [error, setError] = useState<string>('');

  const validateHostname = (value: string): { isValid: boolean; type: 'ip' | 'domain' | null; error?: string } => {
    if (!value.trim()) {
      return { isValid: false, type: null, error: lang === 'zh' ? '請輸入目標' : 'Please enter a target' };
    }

    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (ipRegex.test(value)) {
      return { isValid: true, type: 'ip' };
    }

    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
    if (domainRegex.test(value)) {
      return { isValid: true, type: 'domain' };
    }

    return { isValid: false, type: null, error: lang === 'zh' ? '無效的 IP 或網域名稱' : 'Invalid IP or domain name' };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const parsed = parseTargetInput(target);
    if (!parsed) {
      setError(lang === 'zh' ? '請輸入目標' : 'Please enter a target');
      return;
    }

    const validation = validateHostname(parsed.hostname);
    if (!validation.isValid) {
      setError(validation.error || (lang === 'zh' ? '無效的目標' : 'Invalid target'));
      return;
    }

    const finalPort = port || parsed.port;
    if (finalPort < 1 || finalPort > 65535) {
      setError(lang === 'zh' ? 'Port 必須介於 1 到 65535' : 'Port must be between 1 and 65535');
      return;
    }

    const scanTarget: ScanTarget = {
      type: validation.type!,
      value: parsed.hostname,
      port: finalPort,
    };

    onTargetAdd(scanTarget);
    setTarget('');
    setError('');
  };

  const handleTargetChange = (value: string) => {
    setTarget(value);
    if (error) setError('');
  };

  return (
    <div className="single-target-input">
      <h3>{lang === 'zh' ? '單次掃描' : 'Single Scan'}</h3>
      <form onSubmit={handleSubmit} className="target-form">
        <div className="form-group">
          <label htmlFor="target">{lang === 'zh' ? 'TARGET (IP、DOMAIN 或 URL):' : 'TARGET (IP, DOMAIN or URL):'}</label>
          <input
            id="target"
            type="text"
            value={target}
            onChange={(e) => handleTargetChange(e.target.value)}
placeholder={lang === "zh" ? "e.g., google.com 或 https://google.com" : "e.g., google.com or https://google.com"}
            disabled={disabled}
            className={error ? 'error' : ''}
          />
          {error && <span className="error-message">{error}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="port">Port:</label>
          <input
            id="port"
            type="number"
            value={port}
            onChange={(e) => setPort(parseInt(e.target.value) || 443)}
            min="1"
            max="65535"
            disabled={disabled}
          />
        </div>

        <button
          type="submit"
          disabled={disabled || !target.trim()}
          className="add-target-btn"
        >
          {lang === 'zh' ? 'TLS 掃描' : 'TLS Scan'}
        </button>
      </form>
    </div>
  );
};